'use client'

import { Form, FormGroupLabel, FormStore, useFormContext, useFormStore } from '@ariakit/react'
import { ComponentProps, createContext, useEffect, useLayoutEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

import {
  ColorSwatchWrapper,
  SizeSelectorSelectorButton,
  TypeStyle,
  focusOutline,
  fromMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { MakeCartItemProps, useMakeCartItem } from '../../../brunswick/containers/cart/utils'
import { useAppDirectoryStableQueryParams } from '../../_hooks/useAppDirectoryStableQueryParams'
import { usePageTypeTracking } from '../../_hooks/useStaticTracking'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useFeatureFlag } from '../../_providers/ExperimentationProvider.client'
import { useOrganization } from '../../_providers/OrganizationProvider.client'
import { useTrackingLocation } from '../../_providers/TrackingLocationProvider.client'
import { useTrackProductInteraction } from '../../_providers/TrackingProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { useNextDialogContext } from '../dialogs/NextDialog.client'
import {
  WaitlistModalProvider,
  useWaitlistModal,
} from '../pages/product-pages/shared/WaitlistModal/WaitlistModal'

import type {
  ProductGroupVariantLabels,
  VariantSelectionFormColorOptionFragment,
  VariantSelectionFormFitOptionFragment,
  VariantSelectionFormGroupFieldsFragment,
  VariantSelectionFormSizeOptionFragment,
  VariantSelectionFormVariantFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import type { StaticTrackingSecondaryLocation } from '../../_hooks/useStaticTracking'

export const variantSelectionFormColorOption = gql(`
  fragment VariantSelectionFormColorOption on ProductCatalogProductColorOption {
    color {
      id
      hexCodes
      displayValue
      badge
      badgeExpiration
      swatchImage {
        url
      }
      group {
        id
      }
    }
    compareAtPriceRange {
      min {
        amount
      }
      max {
        amount
      }
      minPercentageOff
      maxPercentageOff
    }
    priceRange {
      currencyCode
      min {
        amount
      }
      max {
        amount
      }
    }
    outOfStock
  }
`)

export const variantSelectionFormFitOption = gql(`
  fragment VariantSelectionFormFitOption on ProductCatalogProductFitOption {
    fit {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormSizeOption = gql(`
  fragment VariantSelectionFormSizeOption on ProductCatalogProductSizeOption {
    size {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormVariantFields = gql(`
  fragment VariantSelectionFormVariantFields on ProductCatalogVariant {
    id
    sku
    stockState
    availability
    shopifyId
    shopifyProductId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

export const variantSelectionFormGroupFields = gql(`
  fragment VariantSelectionFormGroupFields on ProductCatalogVariantGroup {
    id
    title
    availability
    siteWidePromoEligible
    finalSale
    primaryCategory {
      id
      key
    }
    color {
      id
      hexCodes
      displayValue
      group {
        id
      }
    }
    variant(fit: $fitFieldValue, size: $sizeFieldValue) {
      ...VariantSelectionFormVariantFields
    }
  }
`)

// We want to use `on ProductCatalogProduct`... but something is messed up with Apollo Client.
// If we do that, it will ignore fields from this fragment despite the fact they are fields valid on that interface.
// Instead we have to use `on ProductCatalogStandardProduct` which is not an interface but an implementation.
export const variantSelectionFormProductFields = gql(`
  fragment VariantSelectionFormProductFields on ProductCatalogStandardProduct {
    id
    handle
    options(color: $colorFieldValue, fit: $fitFieldValue, size: $sizeFieldValue, collectionHandle: $collectionHandle) {
      colors {
        ...VariantSelectionFormColorOption
      }
      fits {
        ...VariantSelectionFormFitOption
      }
      sizes {
        ...VariantSelectionFormSizeOption
      }
    }
  }
`)

export type VariantSelectionFormContextValue = {
  product: {
    handle: string
    options: {
      colors?: VariantSelectionFormColorOptionFragment[]
      fits?: VariantSelectionFormFitOptionFragment[]
      sizes?: VariantSelectionFormSizeOptionFragment[]
    }
  }
  variantGroup: MakeCartItemProps['variantGroup'] & {
    id: string
    title: string
    color: VariantSelectionFormGroupFieldsFragment['color']
    availability: VariantSelectionFormGroupFieldsFragment['availability']
    primaryCategory: VariantSelectionFormGroupFieldsFragment['primaryCategory'] &
      MakeCartItemProps['variantGroup']['primaryCategory']
  }
  selectedVariant: VariantSelectionFormSelectedVariant | undefined | null
  loading: boolean
  secondaryTrackingLocation: StaticTrackingSecondaryLocation
  sizeLabelOverride?: ProductGroupVariantLabels['size']
}

export const VariantSelectionFormContext = createContext<
  VariantSelectionFormContextValue | undefined
>(undefined)

export type VariantSelectionFormState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
  quantity: number | undefined | null
}

type UseVariantSelectionFormProps<T extends VariantSelectionFormState> = {
  defaultValues: T
  onValuesChange?: (values: T) => void
}

export const useVariantSelectionForm = <T extends VariantSelectionFormState>({
  defaultValues,
  onValuesChange,
}: UseVariantSelectionFormProps<T>) => {
  const formStore = useFormStore<T>({
    defaultValues,
    setValues: onValuesChange,
  })

  const colorFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['color']>('color') ??
    defaultValues.color ??
    ''

  const fitFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['fit']>('fit') ?? ''

  const sizeFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['size']>('size') ?? ''

  return {
    formStore,
    queryVariables: {
      colorFieldValue,
      sizeFieldValue,
      fitFieldValue,
    },
  }
}

export const VariantSelectionFormFields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${o => o.theme.spacing(6)};
`

export type VariantSelectionFieldProps = {
  onChange?: (newValue: string) => void
}

type VariantSelectionFormSelectedVariant =
  | VariantSelectionFormVariantFieldsFragment
  | undefined
  | null

type VariantSelectionFormBasicState = {
  color: string | undefined | null
  fit: string | undefined | null
  size: string | undefined | null
}

type VariantSelectionFormProps<T extends VariantSelectionFormBasicState> = {
  store: FormStore<T>
} & VariantSelectionFormContextProviderProps &
  Omit<ComponentProps<typeof Form>, 'store' | 'onSubmit'>

export const VariantSelectionForm = <T extends VariantSelectionFormBasicState>({
  loading,
  product,
  variantGroup,
  selectedVariant,
  secondaryTrackingLocation,
  sizeLabelOverride,
  isPrimaryFormForPageView,
  makeCartItem,
  onSubmit,
  children,
  ...props
}: VariantSelectionFormProps<T>) => {
  return (
    <Form
      data-testid='variant-picker'
      style={{ display: 'contents' }}
      resetOnSubmit={false}
      {...props}
    >
      <WaitlistModalProvider
        colorDisplayValue={variantGroup.color?.displayValue ?? ''}
        variantShopifyId={selectedVariant?.shopifyId}
        variantSku={selectedVariant?.sku ?? ''}
        productTitle={variantGroup.title}
      >
        <VariantSelectionFormContextProvider
          isPrimaryFormForPageView={isPrimaryFormForPageView}
          makeCartItem={makeCartItem}
          onSubmit={onSubmit}
          loading={loading}
          product={product}
          variantGroup={variantGroup}
          selectedVariant={selectedVariant}
          secondaryTrackingLocation={secondaryTrackingLocation}
          sizeLabelOverride={sizeLabelOverride}
        >
          {children}
        </VariantSelectionFormContextProvider>
      </WaitlistModalProvider>
    </Form>
  )
}

export type VariantSelectionFormSubmitHandler = (args: {
  product: VariantSelectionFormContextValue['product']
  variantGroup: VariantSelectionFormContextValue['variantGroup']
  selectedVariant: VariantSelectionFormSelectedVariant
  quantity: number
}) => void

type VariantSelectionFormContextProviderProps = {
  isPrimaryFormForPageView?: boolean // used for CartInterstitial to gate modals/forms with AddToBag functionality
  makeCartItem?: ReturnType<typeof useMakeCartItem>
  onSubmit?: VariantSelectionFormSubmitHandler
  children?: React.ReactNode
} & VariantSelectionFormContextValue

const VariantSelectionFormContextProvider = ({
  loading: loadingProp,
  product,
  variantGroup,
  selectedVariant,
  secondaryTrackingLocation,
  sizeLabelOverride,
  isPrimaryFormForPageView,
  makeCartItem: makeCartItemProp,
  onSubmit,
  children,
}: VariantSelectionFormContextProviderProps) => {
  const { trackProductInteraction } = useTrackProductInteraction()
  const pageType = usePageTypeTracking()
  const trackingLocation = useTrackingLocation()
  const dialogContext = useNextDialogContext()
  const defaultMakeCartItem = useMakeCartItem()
  const makeCartItem = makeCartItemProp ?? defaultMakeCartItem
  const { addItems, status: cartStatus } = useCart()
  const formContext = useFormContext()
  const { open: openCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const { b2bStorefrontCollection } = useOrganization()
  const { openWaitlistModal } = useWaitlistModal()
  const submittingRef = useRef(false)

  /*
  This is a little hacky, but we do this so this button is not shown as loading on
  page load or if a different add to cart is pressed on the same page as this.
  */
  if (cartStatus === 'resolved') {
    submittingRef.current = false
  }
  const loading = loadingProp || (submittingRef.current === true && cartStatus === 'pending')

  const isDTC = !b2bStorefrontCollection
  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isPrimaryFormForPageView || !isDTC,
  })

  // On the PDP we use two submit buttons. So this code cannot exist with the submit component.
  // If you run `useSubmit` in duplicate components aria kit will submit twice for one of the two components.
  formContext?.useSubmit(state => {
    if (selectedVariant) {
      if (selectedVariant.availability === 'AVAILABLE') {
        submittingRef.current = true
        if (onSubmit) {
          onSubmit({
            product,
            variantGroup,
            selectedVariant,
            quantity: Number(state.values['quantity']) || 1,
          })
        } else {
          const tracking = {
            location: pageType,
            secondary_location: secondaryTrackingLocation,
            section_number: trackingLocation.sectionNumber ?? null,
            position: trackingLocation.position ?? null,
            href: null,
            quickbuy: trackingLocation.isQuickbuy ?? false,
          }
          addItems(
            makeCartItem({
              product,
              variant: selectedVariant,
              variantGroup,
              options: {
                attributedPortal: undefined,
                quantity: Number(state.values['quantity']) || 1,
              },
            }).map(cartItem => ({ ...cartItem, tracking }))
          )
          dialogContext?.hide()
          if (cartInterstitialTestDecision === 'yes-cart-interstitial') {
            openCartInterstitialDialog()
          } else {
            openCartDialog()
          }
        }
      } else if (selectedVariant.availability === 'WAIT_LISTING') {
        openWaitlistModal()
      }
    }
  })

  const selectedVariation = `${variantGroup.color?.id}:${selectedVariant?.size?.id}:${selectedVariant?.fit?.id}`
  const lastSelectedVariationRef = useRef(selectedVariation)
  const isAltered = lastSelectedVariationRef.current != selectedVariation
  lastSelectedVariationRef.current = selectedVariation
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!loading && isAltered && selectedVariant) {
      trackProductInteraction({
        handle: product.handle,
        color: variantGroup.color?.id,
        id: variantGroup.id,
        shopifyId: selectedVariant.shopifyProductId,
        soldOut: variantGroup.availability === 'OUT_OF_STOCK',
      })
    }
  }, [
    isAltered,
    loading,
    product.handle,
    selectedVariant,
    trackProductInteraction,
    variantGroup.availability,
    variantGroup.color?.id,
    variantGroup.id,
  ])

  return (
    <VariantSelectionFormContext.Provider
      value={{
        loading,
        product,
        variantGroup,
        selectedVariant,
        secondaryTrackingLocation,
        sizeLabelOverride,
      }}
    >
      {children}
    </VariantSelectionFormContext.Provider>
  )
}

type VariantSelectionFormQueryParamBindingProps = {
  colorKey: string
  fitKey: string
  sizeKey: string
}

export const VariantSelectionFormAppDirectoryQueryParamBinding = ({
  colorKey,
  fitKey,
  sizeKey,
}: VariantSelectionFormQueryParamBindingProps) => {
  const { query } = useAppDirectoryStableQueryParams()
  useSyncQueryParamToFormField('color', query[colorKey])
  useSyncQueryParamToFormField('fit', query[fitKey])
  useSyncQueryParamToFormField('size', query[sizeKey])
  return null
}

/**
 * Copies a query param into a form field when it changes. Must run after commit, not during render because
 * writing the form store from a router transition's render interrupts that transition, causing a loop
 */
function useSyncQueryParamToFormField(
  field: 'color' | 'fit' | 'size',
  value: string | string[] | undefined
) {
  const formContext = useFormContext()
  const lastValueRef = useRef(value)

  useLayoutEffect(() => {
    if (lastValueRef.current === value) return

    lastValueRef.current = value
    formContext!.setValue(field, value)
  }, [field, formContext, value])
}

export const VariantSelectionOptionsRow = styled.div<{ $grid?: boolean; $wideMin?: boolean }>`
  ${o =>
    o.$grid
      ? `display: grid;
      grid-template-columns: repeat(auto-fill, minmax(${o.$wideMin ? '30%' : '52px'}, 1fr));
      gap: ${o.theme.spacing(2)};
      width: 100%;`
      : `display: flex;
  flex-wrap: wrap;
  gap: ${o.theme.spacing(2)};
  width: 100%;`}
`

export const VariantSelectionOption = styled(SizeSelectorSelectorButton)<{ $noPadding?: boolean }>`
  ${o => (o.$noPadding ? 'padding: 0; min-height: 40px;' : 'min-width: 30px;')}
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const VariantSelectionInvisibleInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;

  &:focus-visible + ${ColorSwatchWrapper}, &:focus-visible + ${VariantSelectionOption} {
    ${focusOutline};
    outline-style: solid;
  }
  margin: 0;
  position: absolute;
`

export const variantSelectionFormGroupLabelTypographyCss = css`
  ${TypeStyle.css.headlineMicro};
  color: ${o => o.theme.color.text.secondary.on.background};
`

export const VariantSelectionFormGroupLabelPrimaryText = styled.span<{
  $isSale?: boolean
}>`
  color: ${o =>
    o.$isSale
      ? o.theme.color.text.critical.on.background
      : o.theme.color.text.primary.on.background};
  ${TypeStyle.css.headlineExtraExtraSmall};
`

export const VariantSelectionFormGroupLabel = styled(FormGroupLabel)<{
  $spaceBetween?: boolean
}>`
  ${variantSelectionFormGroupLabelTypographyCss};
  display: flex;
  ${o => (o.$spaceBetween ? 'justify-content: space-between;' : 'justify-self: flex-start;')}
  gap: ${o => o.theme.spacing(1)};
  flex-wrap: wrap;
  align-items: center;
`

export const VariantSelectionFieldValueDisplay = styled.span`
  display: inline-flex;
  flex: 1;
  min-width: 0;
  text-transform: none;
  ${TypeStyle.css.headlineExtraExtraSmall};
  color: ${o => o.theme.color.text.primary.on.background};
  font-weight: 400;
  ${fromMd} {
    font-weight: 400;
  }
`

export const StyledVariantSelectionFromSoldOutMessage = styled.span`
  color: initial;
  white-space: nowrap;
`

export const VariantSelectionFromSoldOutMessage = () => {
  const translations = useTranslation()
  const message = `- ${translations.detailsSection.soldOutSectionMessage}`

  return (
    <StyledVariantSelectionFromSoldOutMessage>{message}</StyledVariantSelectionFromSoldOutMessage>
  )
}
